import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { listCountsHook, listCountsTags } from "_shared/_api/list-counts/list-counts-hook";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import FeeScheduleCategorySchema from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import { FeeScheduleJSON } from "work/fee-schedules/_schemas/fee-schedule-schema";
import FeeScheduleSchema from "work/fee-schedules/_schemas/fee-schedule-schema";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import {
  FeeScheduleListCountsJSONZ,
  type FeeScheduleListCounts,
} from "work/fee-schedules/_api/queries/_schemas/fee-schedule-list-counts-schema";
import z from "zod";

export type FeeScheduleListItemJSON = FeeScheduleJSON & { id?: string | null };
export type FeeScheduleLoadIssue = { id: Guid; issue: string };

const GuidApiResponseZ = z.union([z.uuid(), z.object({ value: z.uuid() })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const StringApiResponseZ = z.union([z.string(), z.object({ value: z.string() })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const CurrencyApiResponseZ = z.union([z.enum(Currency), z.object({ value: z.enum(Currency) })]).transform((value) => {
  return typeof value === "string" ? value : value.value;
});

const FeeScheduleCategoryApiResponseJSONZ = FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ.extend({
  id: GuidApiResponseZ.nullish(),
  name: StringApiResponseZ,
  description: StringApiResponseZ.nullish(),
  feeCurrency: CurrencyApiResponseZ.nullish(),
  feeUnit: StringApiResponseZ.nullish(),
});

const FeeScheduleApiResponseJSONZ = FeeScheduleSchema.FeeScheduleJSONZ.extend({
  workAgreementId: GuidApiResponseZ,
  categories: z.array(FeeScheduleCategoryApiResponseJSONZ).nullish(),
  clientFeeScheduleTemplateIds: z.array(GuidApiResponseZ).nullish(),
  vendorFeeScheduleTemplateIds: z.array(GuidApiResponseZ).nullish(),
});

export const feeSchedulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFeeSchedulesByQuery: build.query<Paginated<FeeScheduleListItemJSON>, { query: FeeSchedulesQuery; interfaceName: string }>({
      query: ({ query }) => `work/fee-schedules?${query.searchParams.toString()}`,
      rawResponseSchema: z.array(FeeScheduleApiResponseJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: FeeScheduleListItemJSON[], meta): Paginated<FeeScheduleListItemJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.data.map((item) => ({ type: "FeeSchedule" as const, id: item.workAgreementId })) ?? []),
              { type: "FeeSchedule" as const, id: "LIST" },
            ]
          : [{ type: "FeeSchedule" as const, id: "LIST" }],
    }),
    /**
     * The In use tab's counts, of current and of archived work, in one request under the list's own
     * criteria (#6709). `criteria` is the list's filter as search parameters, less the archived state.
     */
    getFeeScheduleListCounts: build.query<FeeScheduleListCounts, { criteria: string }>({
      query: ({ criteria }) => `work/fee-schedules/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: FeeScheduleListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: listCountsTags("FeeSchedule"),
    }),
  }),
});

export const { useGetFeeSchedulesByQueryQuery, useGetFeeScheduleListCountsQuery } = feeSchedulesApi;

/** The In use tab's counts, from its one counts request (#6709). */
export const useFeeScheduleListCounts = listCountsHook<{ criteria: string }, FeeScheduleListCounts>(
  useGetFeeScheduleListCountsQuery,
);
